import { useState } from 'react'
import { 
  ChevronLeft, 
  ChevronRight, 
  Clock,
  CheckCircle,
  Circle,
  AlertCircle
} from 'lucide-react'
import { useProgramStore } from '@/stores/programStore'
import { ProgramCalendarEvent, Program } from '@/types'
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay,
  addMonths,
  subMonths,
  isToday
} from 'date-fns'

interface ProgramCalendarViewProps {
  program?: Program
  showAllPrograms?: boolean
}

export function ProgramCalendarView({ program }: ProgramCalendarViewProps) {
  const {
    calendarEvents,
    selectedDate,
    setSelectedDate
  } = useProgramStore()

  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedEvent, setSelectedEvent] = useState<ProgramCalendarEvent | null>(null)
  const [showEventModal, setShowEventModal] = useState(false)

  // Filter events for the current program if specified
  const filteredEvents = program 
    ? calendarEvents.filter(event => event.clientId === program.clientId)
    : calendarEvents

  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const calendarStart = startOfWeek(monthStart)
  const calendarEnd = endOfWeek(monthEnd)
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd })

  const getEventsForDay = (day: Date) => {
    return filteredEvents.filter(event => isSameDay(new Date(event.start), day))
  }

  const getEventStatusColor = (status: ProgramCalendarEvent['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500 text-white'
      case 'scheduled':
        return 'bg-blue-500 text-white'
      case 'missed':
        return 'bg-red-500 text-white'
      case 'rescheduled':
        return 'bg-yellow-500 text-white'
      default:
        return 'bg-gray-500 text-white'
    }
  }

  const getEventIcon = (event: ProgramCalendarEvent) => {
    switch (event.status) {
      case 'completed':
        return <CheckCircle className="w-3 h-3" />
      case 'missed':
        return <AlertCircle className="w-3 h-3" />
      default:
        return <Circle className="w-3 h-3" />
    }
  }

  const handleEventClick = (event: ProgramCalendarEvent) => {
    setSelectedEvent(event)
    setShowEventModal(true)
  }

  const handleDateClick = (day: Date) => {
    setSelectedDate(day)
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => 
      direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1)
    )
  }

  return (
    <div className="space-y-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold text-gray-900">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            <button
              onClick={() => navigateMonth('next')}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          
          <button
            onClick={() => setCurrentMonth(new Date())}
            className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
          >
            Today
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-xs text-gray-600">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              Completed
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-500 rounded"></div>
              Scheduled
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              Missed
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-3 text-center text-sm font-medium text-gray-500 bg-gray-50">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day) => {
            const events = getEventsForDay(day)
            const isCurrentMonth = isSameMonth(day, currentMonth)
            const isSelected = isSameDay(day, selectedDate)
            const isDayToday = isToday(day)

            return (
              <div
                key={day.toISOString()}
                onClick={() => handleDateClick(day)}
                className={`min-h-[100px] border-r border-b border-gray-200 p-2 cursor-pointer hover:bg-gray-50 ${
                  isSelected ? 'bg-blue-50' : ''
                } ${!isCurrentMonth ? 'bg-gray-50' : ''}`}
              >
                <div className={`text-sm font-medium mb-1 ${
                  !isCurrentMonth ? 'text-gray-400' : 
                  isDayToday ? 'text-blue-600' : 'text-gray-900'
                }`}>
                  {format(day, 'd')}
                  {isDayToday && (
                    <div className="w-1.5 h-1.5 bg-blue-600 rounded-full inline-block ml-1"></div>
                  )}
                </div>

                <div className="space-y-1">
                  {events.slice(0, 3).map((event) => (
                    <div
                      key={event.id}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleEventClick(event)
                      }}
                      className={`text-xs px-2 py-1 rounded text-left cursor-pointer hover:opacity-80 ${
                        getEventStatusColor(event.status)
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        {getEventIcon(event)}
                        <span className="truncate">{event.title}</span>
                      </div>
                    </div>
                  ))}
                  {events.length > 3 && (
                    <div className="text-xs text-gray-500 px-2">
                      +{events.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Selected Date Events */}
      {getEventsForDay(selectedDate).length > 0 && (
        <div className="coach-section">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Events for {format(selectedDate, 'EEEE, MMMM d, yyyy')}
          </h3>
          <div className="space-y-3">
            {getEventsForDay(selectedDate).map((event) => (
              <div
                key={event.id}
                onClick={() => handleEventClick(event)}
                className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${getEventStatusColor(event.status).split(' ')[0]}`}></div>
                    <div>
                      <div className="font-medium text-gray-900">{event.title}</div>
                      <div className="text-sm text-gray-600 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {format(new Date(event.start), 'h:mm a')} - {format(new Date(event.end), 'h:mm a')}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 capitalize">
                    {event.status}
                  </div>
                </div>
                {event.notes && (
                  <div className="text-sm text-gray-600 mt-2">
                    {event.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Event Detail Modal */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Event Details</h2>
                <button
                  onClick={() => setShowEventModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500">Title</div>
                  <div className="font-medium">{selectedEvent.title}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Time</div>
                  <div className="font-medium flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {format(new Date(selectedEvent.start), 'h:mm a')} - {format(new Date(selectedEvent.end), 'h:mm a')}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Status</div>
                  <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm ${getEventStatusColor(selectedEvent.status)}`}>
                    {getEventIcon(selectedEvent)}
                    {selectedEvent.status.charAt(0).toUpperCase() + selectedEvent.status.slice(1)}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Type</div>
                  <div className="font-medium capitalize">{selectedEvent.type}</div>
                </div>
                
                {selectedEvent.notes && (
                  <div>
                    <div className="text-sm text-gray-500">Notes</div>
                    <div className="text-sm text-gray-700">{selectedEvent.notes}</div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setShowEventModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Close
                </button>
                <button className="coach-button-primary px-4 py-2">
                  Edit Event
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}